/**
 * @wlh/learning-bridge — framework-free helpers implementing the HTML Learning
 * Package Bridge on both sides:
 *
 *  - `createHostBridge()` runs in the LMS learner shell (the parent window).
 *    It owns the <iframe>, validates every inbound message's `event.origin`
 *    against the single expected packages origin, and forwards trusted guest
 *    events to a caller-supplied sink (which POSTs to the API for the real
 *    session/enrolment/item checks — this library never trusts a message).
 *
 *  - `createGuestBridge()` is embedded in the HTML package. It only accepts
 *    messages from the known LMS origin and exposes typed `report*` helpers.
 *
 * Contract & security model: docs/html-package-bridge.md
 */
import { BRIDGE_VERSION, GUEST_MESSAGE_TYPES, HOST_MESSAGE_TYPES, } from "@wlh/shared-types";
const GUEST_TYPES = new Set(GUEST_MESSAGE_TYPES);
const HOST_TYPES = new Set(HOST_MESSAGE_TYPES);
function isRecord(v) {
    return typeof v === "object" && v !== null;
}
/** Shallow structural check — the API performs full zod validation server-side. */
export function looksLikeGuestMessage(data) {
    return (isRecord(data) &&
        data["bridgeVersion"] === BRIDGE_VERSION &&
        typeof data["sessionToken"] === "string" &&
        typeof data["type"] === "string" &&
        GUEST_TYPES.has(data["type"]));
}
export function looksLikeHostMessage(data) {
    return (isRecord(data) &&
        data["bridgeVersion"] === BRIDGE_VERSION &&
        typeof data["type"] === "string" &&
        HOST_TYPES.has(data["type"]));
}
export function createHostBridge(options) {
    const win = options.window ?? globalThis.window;
    const listener = (event) => {
        if (event.origin !== options.packagesOrigin)
            return;
        if (!looksLikeGuestMessage(event.data))
            return;
        if (event.data.sessionToken !== options.sessionToken)
            return;
        options.onGuestEvent(event.data);
    };
    win.addEventListener("message", listener);
    const send = (message) => {
        options.frame.contentWindow?.postMessage(message, options.packagesOrigin);
    };
    return {
        init: () => send({
            type: "LMS_INIT",
            bridgeVersion: BRIDGE_VERSION,
            sessionToken: options.sessionToken,
            learningItemId: options.learningItemId,
            resumeState: options.resumeState,
        }),
        send,
        dispose: () => win.removeEventListener("message", listener),
    };
}
function defaultEventId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto)
        return crypto.randomUUID();
    throw new Error("crypto.randomUUID unavailable — pass newEventId to createGuestBridge");
}
export function createGuestBridge(options) {
    const win = options.window ?? globalThis.window;
    const newEventId = options.newEventId ?? defaultEventId;
    let sessionToken = null;
    const listener = (event) => {
        if (event.origin !== options.lmsOrigin)
            return;
        if (!looksLikeHostMessage(event.data))
            return;
        if (event.data.type === "LMS_INIT")
            sessionToken = event.data.sessionToken;
        options.onHostMessage?.(event.data);
    };
    win.addEventListener("message", listener);
    const post = (message) => {
        win.parent.postMessage(message, options.lmsOrigin);
    };
    const requireToken = () => {
        if (sessionToken === null)
            throw new Error("Bridge not initialised — awaiting LMS_INIT");
        return sessionToken;
    };
    return {
        reportStarted: () => {
            // Resolve the token first: a pre-init call must throw without consuming an eventId.
            const sessionToken = requireToken();
            const eventId = newEventId();
            post({ type: "MODULE_STARTED", bridgeVersion: BRIDGE_VERSION, sessionToken, eventId });
            return eventId;
        },
        reportProgress: (percent, currentSection) => {
            const sessionToken = requireToken();
            const eventId = newEventId();
            const clamped = Math.max(0, Math.min(100, percent));
            post({
                type: "PROGRESS_UPDATED",
                bridgeVersion: BRIDGE_VERSION,
                sessionToken,
                eventId,
                percent: clamped,
                ...(currentSection !== undefined ? { currentSection } : {}),
            });
            return eventId;
        },
        saveState: (payload) => {
            const sessionToken = requireToken();
            const eventId = newEventId();
            post({ type: "STATE_SAVE", bridgeVersion: BRIDGE_VERSION, sessionToken, eventId, payload });
            return eventId;
        },
        reportCompleted: () => {
            const sessionToken = requireToken();
            const eventId = newEventId();
            post({ type: "MODULE_COMPLETED", bridgeVersion: BRIDGE_VERSION, sessionToken, eventId });
            return eventId;
        },
        dispose: () => win.removeEventListener("message", listener),
    };
}
//# sourceMappingURL=index.js.map